import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { iconCatalogueList } from '../utils/iconMapping';

const AddIconDialog = ({ open, onClose, onSave, existingKeys, editMapping, saving, t }) => {
  const [selectedKey, setSelectedKey] = useState('');
  const [label, setLabel] = useState('');
  const [search, setSearch] = useState('');

  const isEditing = !!editMapping;

  // Pre-fill when editing
  useEffect(() => {
    if (editMapping) {
      setSelectedKey(editMapping.key || '');
      setLabel(editMapping.label || '');
      setSearch('');
    } else {
      setSelectedKey('');
      setLabel('');
      setSearch('');
    }
  }, [editMapping, open]);

  // Filter out already-mapped icons and apply search
  // When editing, keep the current mapping's key available
  const availableIcons = useMemo(() => {
    const existing = new Set(existingKeys || []);
    return iconCatalogueList.filter(icon => {
      if (isEditing && icon.key === editMapping?.key) return true;
      if (existing.has(icon.key)) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return icon.key.toLowerCase().includes(q) || icon.label.toLowerCase().includes(q);
    });
  }, [existingKeys, search, isEditing, editMapping]);

  const handleSelect = (icon) => {
    setSelectedKey(icon.key);
    if (!isEditing || selectedKey !== icon.key) {
      setLabel(icon.label);
    }
  };

  const handleSave = () => {
    if (selectedKey && label) {
      onSave({ key: selectedKey, label, id: editMapping?.id });
      handleReset();
    }
  };

  const handleReset = () => {
    setSelectedKey('');
    setLabel('');
    setSearch('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing
          ? (t ? t('QbusOutputTypes.edit_icon_mapping') : 'Edit Icon Mapping')
          : (t ? t('QbusOutputTypes.add_icon_mapping') : 'Add Icon Mapping')}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder={t ? t('Common.search') : 'Search...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t ? t('QbusOutputTypes.select_icon') : 'Select an icon'} ({availableIcons.length} {t ? t('QbusOutputTypes.available') : 'available'})
        </Typography>

        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          maxHeight: 320,
          overflow: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 1
        }}>
          {availableIcons.map(({ key, component: Icon, label: iconLabel }) => (
            <Tooltip key={key} title={`${iconLabel} (${key})`}>
              <IconButton
                onClick={() => handleSelect({ key, label: iconLabel })}
                sx={{
                  border: 2,
                  borderColor: selectedKey === key ? 'primary.main' : 'transparent',
                  bgcolor: selectedKey === key ? 'action.selected' : 'action.hover',
                  borderRadius: 1,
                  width: 48,
                  height: 48,
                  '&:hover': {
                    borderColor: selectedKey === key ? 'primary.main' : 'action.disabled'
                  }
                }}
              >
                <Icon color={selectedKey === key ? 'primary' : 'action'} />
              </IconButton>
            </Tooltip>
          ))}
          {availableIcons.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2, width: '100%', textAlign: 'center' }}>
              {t ? t('QbusOutputTypes.no_icons_available') : 'No icons available'}
            </Typography>
          )}
        </Box>

        {selectedKey && (
          <TextField
            fullWidth
            label={t ? t('QbusOutputTypes.icon_label') : 'Label'}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            helperText={`MUI: ${selectedKey}`}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {t ? t('Common.cancel') : 'Cancel'}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedKey || !label || saving}
        >
          {saving ? <CircularProgress size={24} /> : (t ? t('Common.save') : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddIconDialog;
